import React from "react";
import GlobalMenuPopover from "../../../../shared/components/menuPopover";
import { useParticipantDetails } from "../../context/participantDetails";
import { ApiEventLiteForDashboard } from "../../../../shared/types/ApiEventLiteForDashboard";
import { ApiProgramLiteForDashboard } from "../../../../shared/types/ApiProgramLiteForDashboard";
import sortBy from "lodash/sortBy";
import { DISPLAY_ORDER_KEY } from "../../../../shared/utils/constants";
import ActionCard, { ActionClickHandler } from "../../modules/dashboard/components/actionCard";
import getProgramTypeIcon, { getProgramColorStyleProps } from "../../../../shared/utils/getProgramType";
import { useCopies } from "../../../../shared/hooks/useCopies";
import useMyActionsService from "./service";
import { ApiDashboardAction } from "../../../../shared/types/ApiDashboardAction";
import { DashboardActionType } from "../../../../shared/enums/DashboardActionType";
import { ActivityAction } from "../../../../shared/enums/ActivityAction";
import { ActivityStatus } from "../../../../shared/enums/ActivityStatus";
import { useStaticData } from "../../../../context/staticData";
import { positionRight } from "@reach/popover";
import classNames from "classnames";
import { DashboardProgramTypeClassesMap } from "shared/components/programCard/DashboardProgramTypeClassesMap";
import Animations from "shared/components/animations";
import { ApiEventParticipantForDashboard } from "../../../../shared/types/ApiEventParticipantForDashboard";

export default function MyActions() {
  const copies = useCopies();
  const service = useMyActionsService();
  if (service.myActionsCount === 0) {
    return null;
  }

  return (
    <li>
      <GlobalMenuPopover
        key={service.randomKey}
        position={positionRight}
        className="myActions--menu"
        menuTitle={
          <div className="myActions--label">
            <Animations.CircleScaleIn />
            {copies.get("my.actions")}
            <span className="myActions--label_badge">{service.myActionsCount}</span>
          </div>
        }>
        <MyActionsContainer actionClickCallback={service.actionClickCallback} />
      </GlobalMenuPopover>
    </li>
  );
}

function MyActionsContainer({ actionClickCallback }: { actionClickCallback: () => void }) {
  const { participantDetails } = useParticipantDetails();
  return (
    <div className="myActions--container">
      <div className="myActions--genericActions">
        {sortBy(participantDetails?.actions, DISPLAY_ORDER_KEY).map(action => (
          <GenericActionCard
            key={action.dashboardActionType}
            action={action}
            actionClickCallback={actionClickCallback}
          />
        ))}
      </div>
      <div className="myActions--eventActions">
        {participantDetails?.events?.[0] && (
          <MyActionsEventCard eventObj={participantDetails?.events?.[0]} actionClickCallback={actionClickCallback} />
        )}
      </div>
    </div>
  );
}

function MyActionsEventCard(props: { eventObj: ApiEventLiteForDashboard; actionClickCallback: () => void }) {
  return (
    <div className="eventActions--wrapper">
      {sortBy(props.eventObj.actions, DISPLAY_ORDER_KEY).map(action => (
        <GenericActionCard
          key={action.dashboardActionType}
          action={action}
          actionClickCallback={props.actionClickCallback}
          eventParticipant={props.eventObj.eventParticipant}
        />
      ))}
      {sortBy(props.eventObj.programs, DISPLAY_ORDER_KEY).map(programObj => (
        <MyActionsProgramCard
          key={programObj.id}
          programObj={programObj}
          actionClickCallback={props.actionClickCallback}
        />
      ))}
    </div>
  );
}

function MyActionsProgramCard(props: { programObj: ApiProgramLiteForDashboard; actionClickCallback: () => void }) {
  const programTypeIcon = React.useMemo(
    () => getProgramTypeIcon({ programType: props.programObj.programType, programIconImage: props.programObj.icon }),
    [props.programObj.programType, props.programObj.icon]
  );

  if ((props.programObj.actions?.length || 0) === 0) {
    return null;
  }

  return (
    <div
      className={classNames(
        "myActions_programActionCard",
        DashboardProgramTypeClassesMap[props.programObj.programType]
      )}
      style={getProgramColorStyleProps({
        programThemeColor: props.programObj.cardColorHex
      })}>
      <div className="myActions_programActionCard_header">
        <span className="myActions_programActionCard_header__icon">{programTypeIcon ? programTypeIcon : null}</span>
        <h4 className="myActions_programActionCard_header__text">{props.programObj.name}</h4>
      </div>
      <div className="myActions_programActionCard_actionCard">
        {sortBy(props.programObj.actions, DISPLAY_ORDER_KEY).map(actionObj => (
          <ActionCard
            key={actionObj.dashboardActionType}
            action={actionObj}
            metaData={{
              programType: props.programObj.programType,
              programId: props.programObj.id,
              programOrganizationId: actionObj.metadata?.programOrganizationId
            }}
            programParticipants={props.programObj.programParticipants}
            onActionClickCallback={props.actionClickCallback}
          />
        ))}
      </div>
    </div>
  );
}

function GenericActionCard(props: {
  action: ApiDashboardAction;
  actionClickCallback: () => void;
  eventParticipant?: ApiEventParticipantForDashboard;
}) {
  const copies = useCopies();
  const { timelineDashboardActionType, commonDashboardActionType } = useStaticData();
  const dashboardActionTypeCopyMap = React.useMemo(() => {
    return { ...timelineDashboardActionType, ...commonDashboardActionType };
  }, [timelineDashboardActionType, commonDashboardActionType]);

  const copyToShow = React.useMemo(() => {
    switch (props.action.dashboardActionType) {
      case DashboardActionType.MANAGE_TICKETS:
        const pending = props.action.metadata?.pendingAssignment;
        const total = props.action.metadata?.totalPurchased;
        if (total === 0 || pending === 0) {
          return null;
        }
        return copies.get("my.action.unassigned.bulk.ticket.message", {
          0: pending,
          1: total
        });
      case DashboardActionType.PURCHASE_TICKET:
        return copies.get("my.action.ticket.purchase.payment.pending.alert");
      case DashboardActionType.ADDITIONAL_CHARGE:
        return copies.get("my.action.additional.charge.alert");
      default:
        return dashboardActionTypeCopyMap[props.action.dashboardActionType];
    }
  }, [copies, props.action, dashboardActionTypeCopyMap]);

  return (
    <div className="myActions_genericActionCard">
      <div className="myActions_genericActionCard_text">
        <span>{copyToShow}</span>
      </div>
      <div className="myActions_genericActionCard_action">
        <ActionClickHandler
          action={{
            ...props.action,
            activityAction: props.action.activityAction || ActivityAction.GO,
            activityStatus: props.action.activityStatus || ActivityStatus.ACTION_REQUIRED
          }}
          onClickCallback={props.actionClickCallback}
          eventParticipant={props.eventParticipant}
        />
      </div>
    </div>
  );
}
